const queryString = require('query-string');
const pickBy = require('lodash/pickBy');
const omit = require('lodash/omit');
const isEmpty = require('lodash/isEmpty');
const some = require('lodash/some');

const ALLOWED_QS_PARAMS = ['keywords', 'experiences', 'locations', 'positions', 'subjects', 'workplaces', 'contractterms', 'contracttypes', 'salaryranges', 'grades', 'maxdistance', 'displaylocation', 'agerange', 'timing', 'applicationtype', 'excludedLocations'];

const DISTANCE_MAP = { 0: '3', 1: '5', 2: '10', 3: '15', 4: '20', 5: '30', 6: '50', 7: '70', 8: '100', 9: '500' };

const COUNTRY_DISTANCE_MODIFIERS = { au: 'km' };

const SPECIAL_VALUES_FORMAT = {
  applicationtype: () => 'Quick Apply',
  maxdistance: (distance, country) =>
    `${DISTANCE_MAP[~~distance]} ${COUNTRY_DISTANCE_MODIFIERS[country] || 'miles'}`,
  agerange: (interval) => {
    if (interval === '0,19') {
      return '';
    }
    const split = interval.split(',');
    return `${split[0]} to ${split[1] === '19' ? '18+' : split[1]} years old`;
  }
};

const DISTANCE_UNITS_MAP = {
  au: 'km',
  gb: 'miles',
  us: 'miles',
  nz: 'km',
  ie: 'km',
  ca: 'km'
};

const toLowerCaseKeys = (obj) => Object.keys(obj).reduce((newObj, key) => {
  newObj[key.toLowerCase()] = obj[key];
  return newObj;
}, {});

const getAlertTitle = (searchUrl, country = 'gb') => {
  const parsed = toLowerCaseKeys(queryString.parse(searchUrl));
  return Object.keys(parsed).reduce((acc, key) => {
    if (ALLOWED_QS_PARAMS.indexOf(key) === -1) {
      return acc;
    }
    const value = parsed[key];
    const formatter = SPECIAL_VALUES_FORMAT[key.toLowerCase()];
    const finalValue = formatter ? formatter(value, country) : value;
    if (!finalValue) {
      return acc;
    }
    return Array.isArray(finalValue) ?
      [...acc, ...finalValue.map(v => decodeURIComponent(v.trim()))] :
      [...acc, decodeURIComponent(finalValue.trim()).split(':').pop()];
  }, []).join(', ');
};

const createInternalFacet = (category, value, country = 'gb') => {
  const obj = {
    children: []
  };
  switch (category) {
  case 'locations':
    return { ...obj, name: value.split(':').pop(), queryStringId: `${category}=${encodeURIComponent(value)}` };
  case 'maxdistance':
    return {
      ...obj,
      name: `Max distance: ${DISTANCE_MAP[value]} ${DISTANCE_UNITS_MAP[country]}`,
      queryStringId: `${category}=${encodeURIComponent(value)}`
    };
  case 'ageRange': {
    const ageRange = value.split(',');
    return {
      ...obj,
      name: `From ${ageRange[0]} to ${~~ageRange[1] === 19 ? '18+' : ageRange[1]} years old`,
      queryStringId: `${category}=${encodeURIComponent(value)}`
    };
  }
  case 'displayLocation': {
    const [location, point] = value;
    return {
      ...obj,
      name: location,
      queryStringId: `displayLocation=${encodeURIComponent(location)}&point=${encodeURIComponent(point)}`
    };
  }
  default:
    return { ...obj, name: value, queryStringId: `${category}=${encodeURIComponent(value)}` };
  }
};

const decodeObject = (obj) =>
  Object.keys(obj).reduce((decoded, key) => {
    const curr = obj[key];
    decoded[key] = Array.isArray(curr) ? curr.map((el) => decodeURIComponent(el)) : decodeURIComponent(curr);
    return decoded;
  }, {});

const createInternalFacets = (details, country) => {
  // search details expected to be flattened
  // ensure values are decoded (for cancel functionality)
  const detailsObj = decodeObject(details);
  const obj = Object.keys(detailsObj).reduce((facets, category) => {
    if (['sort', 'frequency', 'displayLocation', 'point'].indexOf(category) === -1) {
      const current = detailsObj[category];
      const facet = Array.isArray(current) ?
        current.map((el) => createInternalFacet(category, el, country)) :
        [createInternalFacet(category, current, country)];
      facets.push(...facet);
    }
    if (category === 'displayLocation') {
      const currentDisplayLocation = detailsObj[category];
      const currentPoint = detailsObj.point;
      facets.push(...[createInternalFacet('displayLocation', [currentDisplayLocation, currentPoint])]);
    }
    // @TODO add sort as hidden facet
    return facets;
  }, []);
  return obj;
};

const flattenAlert = (search) => {
  if (!search) return {};
  const flattened = pickBy(omit(search, ['facets', 'geo']));
  const flatten = (obj) => {
    if (obj && !isEmpty(obj)) {
      Object.keys(obj).forEach((key) => {
        if (obj[key] && !isEmpty(obj[key]) && !some(obj[key], isEmpty)) flattened[key] = obj[key];
      });
    }
  };
  flatten(search.facets);
  flatten(search.geo);
  return omit(flattened, 'migratedDistance', 'viewtype');
};

const flattenAlertSearchDetails = (search) => {
  const initialFlattened = flattenAlert(search);
  const flattened = Object.keys(initialFlattened).reduce((obj, key) => {
    const val = initialFlattened[key];
    obj[key] = Array.isArray(val) ? val.map(itm => itm.description) : val;
    obj[key] = key === 'locations' ? obj[key][0] : obj[key];
    return obj;
  }, {});
  return flattened;
};

module.exports = {
  createInternalFacets,
  createInternalFacet,
  getAlertTitle,
  decodeObject,
  flattenAlert,
  flattenAlertSearchDetails
};
